/**
 * Price Histogram API (V2)
 *
 * Backs the price filter's rent-distribution chart: `GET /v2/listing/priceHistogram`
 * returns per-bucket listing counts for a bounding box, honoring the flat V2
 * filter params produced by `toApiParams`.
 *
 * Lives apart from `./listing` because the price filter ships in the search bar
 * on every page (including the homepage hero), and that module carries the full
 * listing-detail surface.
 */

import { apiClient } from '../client/apiClient';

/** Dollar width of one bucket — matches the price slider's step. */
export const PRICE_HISTOGRAM_BUCKET_SIZE = 100;
/** Number of buckets requested; bucket `i` covers `[i * size, (i + 1) * size)`. */
export const PRICE_HISTOGRAM_BUCKET_COUNT = 100;
/** Top of the charted range — also the price slider's max. */
export const PRICE_HISTOGRAM_MAX = PRICE_HISTOGRAM_BUCKET_SIZE * PRICE_HISTOGRAM_BUCKET_COUNT;

export type PriceHistogramParams = {
  minLat: number;
  maxLat: number;
  minLon: number;
  maxLon: number;
} & Record<string, string | number | boolean>;

/** Normalized chart data: one count per bucket, plus the value needed to scale it. */
export type PriceHistogram = {
  /** Counts indexed by bucket, length `PRICE_HISTOGRAM_BUCKET_COUNT`. */
  counts: Array<number>;
  /** Tallest bucket — the 100% reference for bar heights. 0 when nothing matched. */
  maxCount: number;
};

type PriceHistogramBucket = {
  count?: number;
};

/**
 * Turns the API's buckets into chart data.
 *
 * The API appends an overflow bucket at `PRICE_HISTOGRAM_MAX` holding every
 * listing above the charted range. It would dwarf every real bar, so it is
 * dropped — same as the legacy hotpads-web reducer.
 */
function toPriceHistogram(buckets: Array<PriceHistogramBucket>): PriceHistogram {
  const counts: Array<number> = [];
  let maxCount = 0;

  for (const [index, bucket] of buckets.slice(0, PRICE_HISTOGRAM_BUCKET_COUNT).entries()) {
    // The first bucket is "$0-99", which is where listings with no advertised
    // price land. It is large enough to read as a real spike at the left edge
    // and to flatten the rest of the curve, so it is charted as empty. Its slot
    // stays in place to keep the bars aligned with the slider.
    const count = index === 0 ? 0 : (bucket.count ?? 0);
    counts.push(count);
    maxCount = Math.max(maxCount, count);
  }

  return { counts, maxCount };
}

/**
 * Fetch the rent distribution for `params`' bounding box.
 *
 * Price bounds are deliberately not sent: the chart shows the whole range and
 * grays out the buckets outside the active price filter, so it must not be
 * narrowed by that filter.
 */
export async function fetchPriceHistogram(
  params: PriceHistogramParams,
  signal?: AbortSignal,
): Promise<PriceHistogram> {
  const response = await apiClient<{ data?: { buckets?: Array<PriceHistogramBucket> } }>({
    path: '/v2/listing/priceHistogram',
    queryParams: {
      ...params,
      numBuckets: PRICE_HISTOGRAM_BUCKET_COUNT,
      bucketSize: PRICE_HISTOGRAM_BUCKET_SIZE,
      lowPrice: 0,
      useCache: false,
    },
    signal,
  });

  return toPriceHistogram(response.data?.buckets ?? []);
}
